// Site-wide SEO config + per-route metadata.
// Consumed by src/components/Seo.tsx, which relies on React 19 native
// hoisting of <title>/<meta>/<link> into <head>.

export const SITE = {
  url: 'https://ummahcards.com',
  name: 'Ummah Cards',
  titleSuffix: 'Ummah Cards',
  defaultTitle: 'Ummah Cards: Learn Islamic Scholarship Visually',
  defaultDescription:
    'Visual Islamic learning for young Muslim men, with scholar profiles, fiqh maps, hadith tools, and lesson-building tools for teachers and ustadhs.',
  ogImage: 'https://ummahcards.com/icons/icon-512.png',
  // 512x512 square icon → use "summary" (large_image expects ~1200x630).
  twitterCard: 'summary' as const,
} as const;

export interface RouteMeta {
  title: string;
  description: string;
  noindex?: boolean;
}

// Keyed by route path. Titles here are the bare page name; the trailing
// " — Ummah Cards" suffix is appended by the Seo component (except home).
export const ROUTE_META: Record<string, RouteMeta> = {
  '/': {
    title: SITE.defaultTitle,
    description: SITE.defaultDescription,
  },
  '/gallery': {
    title: 'Scholar Gallery',
    description:
      'Browse the full collection of Islamic scholar trading cards — filter by era, school, and rarity to discover the greats of Islamic history.',
  },
  '/binder': {
    title: 'Scholar Binder',
    description:
      'View your collected Islamic scholar cards in a classic binder layout and track your collection.',
  },
  '/fiqh-map': {
    title: 'Interactive Fiqh Map',
    description:
      'Explore the schools of Islamic jurisprudence (madhahib) and how they connect through an interactive visual fiqh map.',
  },
  '/encyclopedia': {
    title: 'Ummah Encyclopedia',
    description: 'Read reviewed articles about Muslim scholars, their teachers, writings, methods, and intellectual legacy.',
  },
  '/hadith-grading': {
    title: 'Hadith Grading',
    description:
      'Understand hadith authenticity grades — sahih, hasan, da‘if and more — with chains of narration laid out visually.',
  },
  '/hadith-routes': {
    title: 'Hadith Route Depth',
    description:
      'Compare the narration routes and chain depth of hadith to see how reports are transmitted across generations.',
  },
  '/about': {
    title: 'About Ummah Cards',
    description:
      'Learn what Ummah Cards is, who it is for, and how it helps teachers and students engage with Islamic scholarship.',
  },
  '/pricing': {
    title: 'Pricing for Teachers & Students',
    description:
      'Pricing plans for Ummah Cards — free to start, with teacher and madrasah tools for classrooms.',
  },
  '/terms': {
    title: 'Terms of Service',
    description: 'Read the Ummah Cards terms for free learning tools and Teacher Pro classroom features.',
  },
  '/privacy': {
    title: 'Privacy Policy',
    description: 'Learn how Ummah Cards handles account, billing, classroom, and student join-code data.',
  },
  '/refunds': {
    title: 'Refund and Cancellation Policy',
    description: 'Review Teacher Pro cancellation and refund information for Ummah Cards.',
  },
  '/contact': {
    title: 'Contact',
    description: 'Contact Ummah Cards support for product, classroom, privacy, and billing questions.',
  },
  '/submit': {
    title: 'Submit a Scholar for Review',
    description:
      'Suggest an Islamic scholar to be added to the Ummah Cards collection. Submissions are reviewed before publishing.',
  },
  // App / private surfaces — keep out of the index.
  '/teacher': {
    title: 'Teacher Workspace',
    description: 'Plan lessons and presentations with Ummah Cards Teacher tools.',
    noindex: true,
  },
  '/login': {
    title: 'Sign In',
    description: 'Sign in to your Ummah Cards account.',
    noindex: true,
  },
  '/account': {
    title: 'Your Account',
    description: 'Manage your Ummah Cards account and subscription.',
    noindex: true,
  },
};

const DEFAULT_META: RouteMeta = {
  title: SITE.defaultTitle,
  description: SITE.defaultDescription,
};

// Exact match first, then prefix match for dynamic routes
// (e.g. /teacher/present/:lessonId). Unknown paths get the default
// metadata and are marked noindex so stray URLs don't get crawled.
export function getRouteMeta(pathname: string): RouteMeta {
  if (ROUTE_META[pathname]) return ROUTE_META[pathname];

  if (pathname.startsWith('/teacher')) {
    return { ...ROUTE_META['/teacher'], noindex: true };
  }

  const prefixMatch = Object.keys(ROUTE_META).find(
    (path) => path !== '/' && pathname.startsWith(path + '/')
  );
  if (prefixMatch) return ROUTE_META[prefixMatch];

  return { ...DEFAULT_META, noindex: true };
}
